/**
 * Range overlay (FE-30 P5).
 *
 * Per-hex `planeGeometry` discs that highlight the tiles within each
 * mech's weapon reach. The visual-engine doesn't import the tool
 * catalog (avoiding a dep on sdk-typescript / sdk-python data); the
 * caller decides the per-side radius. That keeps this component dumb
 * — `rangeA` / `rangeB` is plain world-unit hex distance.
 *
 * Render-side: each tile is a thin disc with `depthWrite: false` so it
 * blends over the floor without Z-fighting. The discs are mounted in
 * a single `<group>` ~0.02 units above the floor.
 */

import { useMemo } from "react";
import type { Hex } from "@pilot-protocol/replay-parser";
import { hexToWorld, ringInRadius, TILE_THICKNESS } from "./hexMath";

export type OverlayMode = "off" | "range";

export interface RangeOverlayProps {
  mode: OverlayMode;
  /** A's position in axial coords. */
  posA: Hex;
  /** B's position in axial coords. */
  posB: Hex;
  /** Reach radius for A in hex steps (e.g. weapon `maxRange`). 0/undefined ⇒ skip. */
  rangeA?: number;
  /** Reach radius for B in hex steps. */
  rangeB?: number;
  /** A's overlay color (defaults to a cyan tint). */
  colorA?: string;
  colorB?: string;
}

const TILE_RADIUS = 0.43;
const TILE_SEGMENTS = 6;
const TILE_OPACITY = 0.18;
const FLOOR_OFFSET = TILE_THICKNESS + 0.02;
const DEFAULT_COLOR_A = "#38bdf8";
const DEFAULT_COLOR_B = "#f97316";

function hexKey(h: Hex): string {
  return `${h.q},${h.r}`;
}

export function RangeOverlay({
  mode,
  posA,
  posB,
  rangeA,
  rangeB,
  colorA = DEFAULT_COLOR_A,
  colorB = DEFAULT_COLOR_B,
}: RangeOverlayProps) {
  const tilesA = useMemo(() => collectTiles(posA, rangeA), [posA.q, posA.r, rangeA]); // eslint-disable-line react-hooks/exhaustive-deps
  const tilesB = useMemo(() => collectTiles(posB, rangeB), [posB.q, posB.r, rangeB]); // eslint-disable-line react-hooks/exhaustive-deps

  if (mode !== "range") return null;
  // Tiles in both sides' reach are rendered once (with the A color) to
  // avoid double-opacity stacking; consumers wanting overlap detection
  // would compute that themselves from `rangeA` + `rangeB`.
  const seenA = new Set(tilesA.map(hexKey));

  return (
    <group name="range-overlay">
      {tilesA.map((h, i) => (
        <RangeTile key={`A-${h.q}-${h.r}-${i}`} hex={h} color={colorA} />
      ))}
      {tilesB.map((h, i) =>
        seenA.has(hexKey(h)) ? null : (
          <RangeTile key={`B-${h.q}-${h.r}-${i}`} hex={h} color={colorB} />
        ),
      )}
    </group>
  );
}

interface RangeTileProps {
  hex: Hex;
  color: string;
}

function RangeTile({ hex, color }: RangeTileProps) {
  const w = hexToWorld(hex, 0);
  return (
    <mesh position={[w.x, FLOOR_OFFSET, w.z]} rotation={[-Math.PI / 2, 0, 0]}>
      <circleGeometry args={[TILE_RADIUS, TILE_SEGMENTS]} />
      <meshBasicMaterial color={color} transparent opacity={TILE_OPACITY} depthWrite={false} />
    </mesh>
  );
}

function collectTiles(center: Hex, radius: number | undefined): Hex[] {
  if (!radius || radius <= 0) return [];
  // ringInRadius returns offsets from origin; shift them by `center`.
  const ring = ringInRadius(radius);
  const out: Hex[] = [];
  for (const r of ring) {
    if (r.q === 0 && r.r === 0) continue; // skip the mech's own tile
    out.push({ q: center.q + r.q, r: center.r + r.r });
  }
  return out;
}
